import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Rocket, X, Loader2, Globe, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export function DeployModal({ isOpen, onClose, project, onSuccess }) {
  const [name, setName] = useState(project?.name || '');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('confirm'); // confirm, deploying, success

  const handleDeploy = async () => {
    if (!name.trim()) {
      toast.error('Ingresa un nombre para el despliegue');
      return;
    }

    setLoading(true);
    setStep('deploying');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/deployments`, {
        project_id: project.id,
        name: name.trim(),
        description: description.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setStep('success');
      toast.success('¡Despliegue exitoso!');
      
      setTimeout(() => {
        onSuccess?.(response.data);
        onClose();
      }, 2000);
      
    } catch (e) {
      setStep('confirm');
      toast.error(e.response?.data?.detail || 'Error al desplegar');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="w-full max-w-md glass rounded-2xl overflow-hidden"
          onClick={e => e.stopPropagation()}
          data-testid="deploy-modal"
        >
          {step === 'confirm' && (
            <>
              {/* Header */}
              <div className="flex items-center justify-between p-5 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-[#FF6B00]/20">
                    <Rocket className="w-5 h-5 text-[#FF6B00]" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">Desplegar Proyecto</h2>
                    <p className="text-xs text-gray-500">Crea un preview público de tu código</p>
                  </div>
                </div>
                <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-gray-400">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-5 space-y-4">
                {/* Warning */}
                <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="text-amber-300 font-medium">Nota importante</p>
                    <p className="text-amber-400/70 text-xs mt-1">
                      El despliegue creará una URL pública. Asegúrate de que el código no contenga información sensible.
                    </p>
                  </div>
                </div>

                {/* Form */}
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Nombre del despliegue</label>
                    <input
                      type="text"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      placeholder="Mi app increíble"
                      className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-[#FF6B00]/50 focus:outline-none"
                      data-testid="deploy-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Descripción (opcional)</label>
                    <textarea
                      value={description}
                      onChange={e => setDescription(e.target.value)}
                      placeholder="Una breve descripción de tu proyecto..."
                      rows={3}
                      className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-[#FF6B00]/50 focus:outline-none resize-none"
                      data-testid="deploy-description-input"
                    />
                  </div>
                </div>

                {/* Project info */}
                <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                  <p className="text-xs text-gray-500">Proyecto fuente:</p>
                  <p className="text-sm text-white font-medium">{project?.name}</p>
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 p-5 border-t border-white/10">
                <button
                  onClick={onClose}
                  className="px-4 py-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeploy}
                  disabled={loading || !name.trim()}
                  className="flex items-center gap-2 px-5 py-2 rounded-lg bg-[#FF6B00] text-white font-medium hover:bg-[#FF8533] disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="confirm-deploy-btn"
                >
                  <Rocket className="w-4 h-4" />
                  Desplegar
                </button>
              </div>
            </>
          )}

          {step === 'deploying' && (
            <div className="p-12 text-center">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#FF6B00]/20 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-[#FF6B00]" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Desplegando...</h3>
              <p className="text-gray-500">Extrayendo código y creando preview</p>
            </div>
          )}

          {step === 'success' && (
            <div className="p-12 text-center">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
                <Globe className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">¡Despliegue Exitoso!</h3>
              <p className="text-gray-500">Tu proyecto está en línea</p>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default DeployModal;
